import {
    MultiViewCalendar,
    CalendarCellProps,
    CalendarProps,
    DatePicker,
    DatePickerChangeEvent,
} from "@progress/kendo-react-dateinputs"
import {FieldRenderProps} from "@progress/kendo-react-form"
import dayjs from "dayjs"
import React, {createContext, useEffect, useRef, useState} from "react"
import {classNames} from "@progress/kendo-react-common"
import {Tooltip} from "@progress/kendo-react-tooltip"
import {RootState} from "../../../rootReducer"
import {useAppSelector} from "../../../store"

const DateContext = createContext({
    testDate: new Date(),
    updateDatePickerValue: (date: Date) => {},
})

const CustomCell = (props: CalendarCellProps) => {
    const dateView = props.view === 0
    const schedulesForMOT = useAppSelector((state: RootState) => state.application.schedulesForMOT)
    const {updateDatePickerValue} = React.useContext(DateContext)
    const noScheduleTitle = "No Schedule for the day"
    const scheduleIndex = schedulesForMOT
        .map(x => dayjs(x.startDateTime).format("DD/MM/YYYY"))
        .indexOf(dayjs(props.value).format("DD/MM/YYYY"))

    const hasScheduleForDay = scheduleIndex >= 0

    const handleClick = () => {
        if (hasScheduleForDay && dateView) {
            if (props.onClick) {
                props.onClick(props.value)
                updateDatePickerValue(props.value)
            }
        }
    }

    const style = {
        cursor: !hasScheduleForDay && dateView ? "not-allowed" : "pointer",
        opacity: !hasScheduleForDay && dateView ? 0.6 : 1,
    }

    const className = classNames({
        "k-calendar-td": true,
        "k-selected": props.isSelected,
        "k-today": props.isToday,
        "k-focus": props.isFocused,
    })

    const titleValue: string = !hasScheduleForDay && dateView ? noScheduleTitle : ""
    return (
        <td onClick={handleClick} className={className}>
            <span className="k-link" style={style} title={titleValue}>
                {props.children}
            </span>
        </td>
    )
}

const CustomCalendar = (props: CalendarProps) => {
    const {min, value, focusedDate} = props
    const {updateDatePickerValue} = React.useContext(DateContext)
    const onchangeCalendar = (ev: any) => {
        updateDatePickerValue(ev.value)
    }

    return (
        <Tooltip anchorElement="target" position="top">
            <MultiViewCalendar
                onChange={onchangeCalendar}
                views={1}
                bottomView="month"
                topView="month"
                cell={CustomCell}
                value={value}
                min={min}
                focusedDate={focusedDate}
            />
        </Tooltip>
    )
}

const TestDatePicker = (fieldRenderProps: FieldRenderProps) => {
    const schedulesForMOT = useAppSelector((state: RootState) => state.application.schedulesForMOT)
    const {name, label, val, min, disabled, updateStateValue} = fieldRenderProps

    const [showDatePicker, setShowDatePicker] = React.useState<boolean>(false)
    const [isValidTestDate, setIsValidTestDate] = useState<boolean>(true)
    const [testDate] = React.useState<Date>(dayjs(val).toDate())

    const datePickerRef = useRef<any>()

    const hasScheduleForDay =
        schedulesForMOT
            .map(x => dayjs(x.startDateTime).format("DD/MM/YYYY"))
            .indexOf(dayjs(val).format("DD/MM/YYYY")) >= 0

    const updateDatePickerValue = (testDate: Date) => {
        updateStateValue(testDate)
        setShowDatePicker(false)
    }

    useEffect(() => {
        setIsValidTestDate(hasScheduleForDay)
    }, [hasScheduleForDay])

    const onDateChange = (event: DatePickerChangeEvent) => {
        if (dayjs(event.target.value).startOf("day") >= dayjs().startOf("day")) {
            if (event.nativeEvent.target.tagName === "INPUT") {
                return false
            }
        }
    }

    const onDateBlur = (event: any) => {
        setShowDatePicker(false)
    }

    const handleKeyDown = (ev: any) => {
        ev.preventDefault()
    }

    React.useEffect(() => {
        if (datePickerRef.current !== null) {
            datePickerRef.current.element.addEventListener("keydown", handleKeyDown)
        }
    }, [datePickerRef])

    React.useEffect(
        () => () => {
            if (datePickerRef.current !== null) {
                datePickerRef.current.element.removeEventListener("keydown", handleKeyDown)
            }
        },
        [datePickerRef],
    )

    const dateProviderProps = {testDate, updateDatePickerValue}
    return (
        <DateContext.Provider value={dateProviderProps}>
            <DatePicker
                ref={datePickerRef}
                name={name}
                label={label}
                min={min}
                disabled={disabled}
                show={showDatePicker}
                value={testDate}
                onFocus={() => {
                    setShowDatePicker(true)
                }}
                onBlur={onDateBlur}
                onChange={onDateChange}
                calendar={CustomCalendar}
                valid={isValidTestDate}
                focusedDate={testDate}
                validationMessage={!hasScheduleForDay ? "No Schedule for the day" : ""}
            />
        </DateContext.Provider>
    )
}

export default TestDatePicker
